import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Plus, Trash2, RefreshCw, Target } from 'lucide-react';
import { API } from '../../App';
import { useAlert } from '../../context/AlertContext';

const GOAL_TYPES = [
  { id: 'reduction_percent', label: 'Reduction %' },
  { id: 'absolute_target',   label: 'Absolute target (tCO2e)' },
  { id: 'net_zero',          label: 'Net Zero' },
  { id: 'sbti_aligned',      label: 'SBTi-aligned' },
];
const SCOPES = ['', 'scope_1', 'scope_2', 'scope_3', 'all'];

export default function GoalsPanel({ siteId }) {
  const { showSuccess, showError } = useAlert();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showCreate, setShowCreate] = useState(false);

  const fetchSummary = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/mrv/goals/summary`);
      setSummary(res.data);
    } catch (e) {
      showError(e.response?.data?.detail || 'Failed to load goals');
    } finally {
      setLoading(false);
    }
  }, [showError]);

  useEffect(() => { fetchSummary(); }, [fetchSummary]);

  const updateField = async (id, field, value) => {
    try {
      await axios.patch(`${API}/mrv/goals/${id}`, { [field]: value });
      fetchSummary();
    } catch (e) {
      showError(e.response?.data?.detail || 'Update failed');
    }
  };

  const deleteGoal = async (id) => {
    if (!window.confirm('Delete this goal?')) return;
    try {
      await axios.delete(`${API}/mrv/goals/${id}`);
      showSuccess('Goal deleted');
      fetchSummary();
    } catch (e) {
      showError(e.response?.data?.detail || 'Delete failed');
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-gray-900">Emission goals</h2>
        <div className="flex gap-2">
          <button onClick={() => setShowCreate(s => !s)}
                  className="px-3 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-md text-sm font-medium inline-flex items-center gap-1">
            <Plus className="w-4 h-4" /> New goal
          </button>
          <button onClick={fetchSummary} disabled={loading} className="p-2 text-gray-500 hover:text-gray-700">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {showCreate && (
        <CreateGoalForm
          siteId={siteId}
          onCreated={() => { setShowCreate(false); fetchSummary(); }}
          onCancel={() => setShowCreate(false)}
        />
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {(summary?.goals || []).length === 0 && (
          <div className="lg:col-span-2 text-center text-gray-500 py-12 border border-dashed border-gray-300 rounded">
            <Target className="w-8 h-8 mx-auto text-gray-300 mb-2" />
            No goals defined yet
          </div>
        )}
        {(summary?.goals || []).map(g => (
          <GoalCard key={g.id} goal={g} onUpdate={updateField} onDelete={deleteGoal} />
        ))}
      </div>
    </div>
  );
}

function GoalCard({ goal, onUpdate, onDelete }) {
  const progress = goal.progress_percent || 0;
  const colorClass =
    progress >= 75 ? 'bg-emerald-500' :
    progress >= 50 ? 'bg-blue-500' :
    progress >= 25 ? 'bg-amber-500' :
                     'bg-red-500';

  return (
    <div className="p-4 bg-white border border-gray-200 rounded-lg">
      <div className="flex items-start justify-between mb-2">
        <div>
          <div className="font-semibold text-gray-900">{goal.goal_name}</div>
          <div className="text-xs text-gray-500">
            {goal.goal_type} {goal.scope ? `· ${goal.scope}` : ''} · target {goal.target_year}
          </div>
        </div>
        <div className="flex items-center gap-1">
          <select value={goal.status}
                  onChange={e => onUpdate(goal.id, 'status', e.target.value)}
                  className="text-xs border rounded px-1.5 py-0.5 bg-white">
            <option value="active">Active</option>
            <option value="paused">Paused</option>
            <option value="completed">Completed</option>
            <option value="archived">Archived</option>
          </select>
          <button onClick={() => onDelete(goal.id)} className="text-red-500 hover:text-red-700 p-1">
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-2 text-xs my-3">
        <div>
          <div className="text-gray-500">Baseline</div>
          <div className="font-mono font-medium">
            {goal.baseline_value != null ? Number(goal.baseline_value).toFixed(0) : '—'} {goal.unit}
          </div>
          <div className="text-[10px] text-gray-400">{goal.baseline_year || '—'}</div>
        </div>
        <div>
          <div className="text-gray-500">Current</div>
          <div className="font-mono font-medium">
            {goal.current_value != null ? Number(goal.current_value).toFixed(0) : '—'}
          </div>
        </div>
        <div>
          <div className="text-gray-500">Target</div>
          <div className="font-mono font-medium text-emerald-700">
            {Number(goal.target_value).toFixed(0)} {goal.unit}
          </div>
          <div className="text-[10px] text-gray-400">{goal.target_year}</div>
        </div>
      </div>

      <div>
        <div className="flex justify-between text-xs mb-1">
          <span className="text-gray-600">Progress</span>
          <span className="font-mono">{progress.toFixed(1)}%</span>
        </div>
        <div className="w-full bg-gray-200 rounded h-2">
          <div className={`${colorClass} h-2 rounded transition-all`} style={{ width: `${progress}%` }} />
        </div>
      </div>

      {goal.description && (
        <div className="text-xs text-gray-500 mt-2">{goal.description}</div>
      )}
    </div>
  );
}

function CreateGoalForm({ siteId, onCreated, onCancel }) {
  const { showError, showSuccess } = useAlert();
  const cy = new Date().getFullYear();
  const [form, setForm] = useState({
    goal_name: 'Net Zero by 2050',
    goal_type: 'reduction_percent',
    scope: '',
    baseline_year: cy - 1,
    baseline_value: '',
    target_year: 2050,
    target_value: '0',
    unit: 'tCO2e',
    description: '',
  });
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!form.goal_name) { showError('Goal name required'); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/mrv/goals`, {
        site_id: siteId || null,
        goal_name: form.goal_name,
        goal_type: form.goal_type,
        scope: form.scope || null,
        baseline_year: form.baseline_year ? parseInt(form.baseline_year) : null,
        baseline_value: form.baseline_value ? parseFloat(form.baseline_value) : null,
        target_year: parseInt(form.target_year),
        target_value: parseFloat(form.target_value),
        unit: form.unit,
        description: form.description || null,
      });
      showSuccess('Goal created');
      onCreated();
    } catch (e) {
      showError(e.response?.data?.detail || 'Create failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mb-4 p-4 border border-emerald-200 bg-emerald-50 rounded-md">
      <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-4 gap-2">
        <input placeholder="Goal name" value={form.goal_name}
               onChange={e => setForm(f => ({ ...f, goal_name: e.target.value }))}
               className="px-2 py-1 text-sm border rounded sm:col-span-2" />
        <select value={form.goal_type}
                onChange={e => setForm(f => ({ ...f, goal_type: e.target.value }))}
                className="px-2 py-1 text-sm border rounded">
          {GOAL_TYPES.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
        </select>
        <select value={form.scope}
                onChange={e => setForm(f => ({ ...f, scope: e.target.value }))}
                className="px-2 py-1 text-sm border rounded">
          {SCOPES.map(s => <option key={s} value={s}>{s || '(all scopes)'}</option>)}
        </select>
        <input type="number" placeholder="Baseline year" value={form.baseline_year}
               onChange={e => setForm(f => ({ ...f, baseline_year: e.target.value }))}
               className="px-2 py-1 text-sm border rounded" />
        <input type="number" step="any" placeholder="Baseline value"
               value={form.baseline_value}
               onChange={e => setForm(f => ({ ...f, baseline_value: e.target.value }))}
               className="px-2 py-1 text-sm border rounded" />
        <input type="number" placeholder="Target year" value={form.target_year}
               onChange={e => setForm(f => ({ ...f, target_year: e.target.value }))}
               className="px-2 py-1 text-sm border rounded" />
        <input type="number" step="any" placeholder="Target value"
               value={form.target_value}
               onChange={e => setForm(f => ({ ...f, target_value: e.target.value }))}
               className="px-2 py-1 text-sm border rounded" />
        <input placeholder="Description" value={form.description}
               onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
               className="px-2 py-1 text-sm border rounded sm:col-span-3 lg:col-span-4" />
      </div>
      <div className="mt-2 flex gap-2 justify-end">
        <button onClick={onCancel} className="px-3 py-1 text-sm text-gray-600 hover:text-gray-800">Cancel</button>
        <button onClick={submit} disabled={saving}
                className="px-3 py-1 bg-emerald-600 text-white rounded text-sm font-medium disabled:bg-gray-300">
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>
    </div>
  );
}
